/**
 * SOFSEK — Rule Matrix Configurator (§2). A conflict rule is strictly a PAIR of
 * bundles (locked v1 decision — no 3+-way rules). Selecting a rule shows a live
 * preview of current matches via sfk_scan_rule_violations() — read-only; Phase 4
 * persists confirmed hits into sfk_violation with full lifecycle.
 */
import { Fragment, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiDb } from '../../lib/api_client';
import { routedInsert, routedUpdate } from '../../lib/routed_writes';
import { hasAccess, type AuthSession } from '../../types/auth';
import { Modal } from '../../components/common/Modal';
import { EmptyState } from '../../components/common/EmptyState';
import { FacetFilter } from '../../components/common/FacetFilter';
import { ClearFiltersButton } from '../../components/common/ClearFiltersButton';
import { YesNoBadge } from '../../components/common/YesNoBadge';
import { RelatedChildrenPanel, RelatedExpandButton, useExpandedRows, type RelatedChildGroup } from '../../components/common/RelatedChildrenPanel';
import { buildTranslationsGroup } from '../../components/common/translations_group';
import { useActiveLocale } from '../../lib/use_active_locale';
import { PageHeader } from '../../components/common/PageHeader';
import { StatStrip } from '../../components/common/StatStrip';
import { ContentCard } from '../../components/common/ContentCard';
import { Plus, RefreshCw, Search } from 'lucide-react';
import { TranslationsEditor } from '../../components/common/TranslationsEditor';
import type { TranslationRow } from '../../components/common/TranslationsEditor';
import { PersonalizationProvider, PersonalizationToggle, PersonalizedGrid, PersonalizedField, GRID_TABLE_CLASS } from '../../components/personalization';
import type { GridColumnDef } from '../../components/personalization';

const PAGE_CODE = 'SFK.CONFLICT_RULE.LIST';
const ACTIVE_LOCALES = ['EN_US', 'ES_MX', 'EN_GB'];
const inputCls = 'mb-3 h-8 w-full rounded border border-csm-border px-2 text-csm-sm';
const labelCls = 'mb-1 block text-csm-sm font-medium text-csm-ink-soft';

interface CodeOpt { code: string; name: string; }
interface RuleRow {
  rule_id: number;
  internal_code: string;
  rule_severity_code: string;
  rule_op_state_code: string;
  effective_start_date: string;
  effective_end_date: string | null;
  is_active: 'Y' | 'N';
  tl: { locale_code: string; display_text: string }[];
}
interface RuleBundleRow { rule_bundle_id: number; rule_id: number; bundle_id: number; side_ordinal: 1 | 2; }

const GRID_COLS: GridColumnDef[] = [
  { key: 'display_text', header: 'Rule', defaultWidth: 220 },
  { key: 'internal_code', header: 'Code', defaultWidth: 160 },
  { key: 'severity', header: 'Severity', defaultWidth: 120 },
  { key: 'op_state', header: 'State', defaultWidth: 120 },
  { key: 'sides', header: 'Bundle A vs Bundle B', defaultWidth: 260 },
  { key: 'is_active', header: 'Active', defaultWidth: 90 },
];

function tlName(list: { locale_code: string; display_text: string }[], loc: string): string {
  return list.find(x => x.locale_code === loc)?.display_text ?? list.find(x => x.locale_code === 'EN_US')?.display_text ?? '';
}

function ConflictRuleInner({ session }: { session: AuthSession }) {
  const { t } = useTranslation();
  // §17.B14 — names each language in the reader's own language.
  const locale = useActiveLocale();
  const [rules, setRules] = useState<RuleRow[]>([]);
  const [ruleBundles, setRuleBundles] = useState<RuleBundleRow[]>([]);
  const [bundles, setBundles] = useState<CodeOpt[]>([]);
  const [severities, setSeverities] = useState<CodeOpt[]>([]);
  const [opStates, setOpStates] = useState<CodeOpt[]>([]);
  const [filter, setFilter] = useState('');
  const [severityFacet, setSeverityFacet] = useState<string[]>([]);
  const [activeFacet, setActiveFacet] = useState<string[]>(['Y']);
  const detail = useExpandedRows<number>();
  // §6/§17: csm_role_page_access decides which write affordances this user is
  // offered. The server refuses the write regardless (RLS, via the Common
  // Routine) — this stops the page offering an action that can only error.
  const canAdd    = hasAccess(session, PAGE_CODE, 'add');
  const canChange = hasAccess(session, PAGE_CODE, 'change');
  const [selected, setSelected] = useState<RuleRow | null>(null);
  const [preview, setPreview] = useState<{ app_user_id: number | null; self_user_id: number | null; matched_permission_ids_a: number[]; matched_permission_ids_b: number[] }[] | null>(null);
  const [previewBusy, setPreviewBusy] = useState(false);
  const [editing, setEditing] = useState<RuleRow | null>(null);
  const [creating, setCreating] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reload = async () => {
    setBusy(true); setError(null);
    try {
      const [ruleRes, rbRes, bundleRes, sevRes, opRes] = await Promise.all([
        apiDb.from('sfk_conflict_rule').select('rule_id, internal_code, rule_severity_code, rule_op_state_code, effective_start_date, effective_end_date, is_active, sfk_conflict_rule_tl ( locale_code, display_text )').order('internal_code'),
        apiDb.from('sfk_rule_bundle').select('rule_bundle_id, rule_id, bundle_id, side_ordinal'),
        apiDb.from('sfk_bundle').select('bundle_id, sfk_bundle_tl ( locale_code, display_text )'),
        apiDb.from('sfk_rule_severity').select('rule_severity_code, sfk_rule_severity_tl ( locale_code, display_text )'),
        apiDb.from('sfk_rule_op_state').select('rule_op_state_code, sfk_rule_op_state_tl ( locale_code, display_text )'),
      ]);
      if (ruleRes.error) throw ruleRes.error;
      if (rbRes.error) throw rbRes.error;
      if (bundleRes.error) throw bundleRes.error;
      if (sevRes.error) throw sevRes.error;
      if (opRes.error) throw opRes.error;
      setRules(((ruleRes.data ?? []) as unknown as Record<string, unknown>[]).map(r => ({
        rule_id: r.rule_id as number,
        internal_code: r.internal_code as string,
        rule_severity_code: r.rule_severity_code as string,
        rule_op_state_code: r.rule_op_state_code as string,
        effective_start_date: r.effective_start_date as string,
        effective_end_date: r.effective_end_date as string | null,
        is_active: r.is_active as 'Y' | 'N',
        tl: (r.sfk_conflict_rule_tl ?? []) as RuleRow['tl'],
      })));
      setRuleBundles((rbRes.data ?? []) as unknown as RuleBundleRow[]);
      setBundles(((bundleRes.data ?? []) as unknown as Record<string, unknown>[]).map(r => ({
        code: String(r.bundle_id), name: tlName((r.sfk_bundle_tl ?? []) as RuleRow['tl'], 'EN_US'),
      })));
      setSeverities(((sevRes.data ?? []) as unknown as Record<string, unknown>[]).map(r => ({
        code: r.rule_severity_code as string, name: tlName((r.sfk_rule_severity_tl ?? []) as RuleRow['tl'], 'EN_US') || (r.rule_severity_code as string),
      })));
      setOpStates(((opRes.data ?? []) as unknown as Record<string, unknown>[]).map(r => ({
        code: r.rule_op_state_code as string, name: tlName((r.sfk_rule_op_state_tl ?? []) as RuleRow['tl'], 'EN_US') || (r.rule_op_state_code as string),
      })));
    } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  };

   
  useEffect(() => { void reload(); }, []);

  const bundleName = (id: number) => bundles.find(b => b.code === String(id))?.name ?? String(id);
  const resolveNameForSeverity = (code: string) => severities.find(s => s.code === code)?.name ?? code;
  const resolveNameForOpState = (code: string) => opStates.find(s => s.code === code)?.name ?? code;
  const sidesOf = (ruleId: number) => {
    const a = ruleBundles.find(rb => rb.rule_id === ruleId && rb.side_ordinal === 1);
    const b = ruleBundles.find(rb => rb.rule_id === ruleId && rb.side_ordinal === 2);
    return { a, b };
  };

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return rules.filter(r =>
      (!q || r.internal_code.toLowerCase().includes(q) || tlName(r.tl, 'EN_US').toLowerCase().includes(q)) &&
      (severityFacet.length === 0 || severityFacet.includes(r.rule_severity_code)) &&
      (activeFacet.length === 0 || activeFacet.includes(r.is_active)));
  }, [rules, filter, severityFacet, activeFacet]);

  // §17.B13 — related children: the bundles on each side of the rule.
  const bundlesOnSide = (ruleId: number, side: 1 | 2) => ruleBundles.filter(rb => rb.rule_id === ruleId && rb.side_ordinal === side);
  const hasRelated = (ruleId: number) => ruleBundles.some(rb => rb.rule_id === ruleId)
    || (rules.find(r => r.rule_id === ruleId)?.tl.length ?? 0) > 0;
  const buildRelatedGroups = (ruleId: number): RelatedChildGroup[] => [
    { key: 'sideA', title: t('sfk.conflict_rule.side_a', 'Side A duties'),
      items: bundlesOnSide(ruleId, 1).map(rb => ({ id: rb.rule_bundle_id, label: bundleName(rb.bundle_id) })) },
    { key: 'sideB', title: t('sfk.conflict_rule.side_b', 'Side B duties'),
      items: bundlesOnSide(ruleId, 2).map(rb => ({ id: rb.rule_bundle_id, label: bundleName(rb.bundle_id) })) },

    buildTranslationsGroup(rules.find(r => r.rule_id === ruleId)?.tl, t, locale),
  ];

  const runPreview = async (rule: RuleRow) => {
    setSelected(rule); setPreview(null); setPreviewBusy(true); setError(null);
    try {
      const { data, error: e } = await apiDb.rpc('sfk_scan_rule_violations', { p_rule_id: rule.rule_id });
      if (e) throw e;
      setPreview((data ?? []) as never);
    } catch (e) { setError((e as Error).message); } finally { setPreviewBusy(false); }
  };

  return (
    <div className="flex h-full flex-col">
      <div className="flex min-h-0 flex-1 flex-col gap-[18px] overflow-auto p-6">
        <PageHeader
          title={t('sfk.conflict_rule.title', 'Rule Matrix Configurator')}
          description={t('sfk.conflict_rule.description', 'Segregation-of-duties rules — the duty bundles that must not be held together.')}
          actions={<>
            <button type="button" onClick={() => void reload()} className="btn-secondary flex items-center gap-1.5"><RefreshCw className="h-3.5 w-3.5" /> {t('common.refresh', 'Refresh')}</button>
            {canAdd && (<button type="button" onClick={() => setCreating(true)} className="btn-primary flex items-center gap-1.5"><Plus className="h-3.5 w-3.5" /> {t('common.new', 'New')}</button>)}
          </>}
        />
        <StatStrip stats={[
          { value: rules.length, label: t('sfk.conflict_rule.stat.total', 'Rules') },
          { value: rules.filter(r => r.is_active === 'Y').length, label: t('common.boolean.active', 'Active') },
        ]} />
        {error && <div className="rounded-csm-card border border-csm-danger-border bg-csm-danger-soft px-3 py-1.5 text-csm-xs text-csm-danger">{error}</div>}
        <ContentCard className="flex min-h-0 flex-1 flex-col">
          <div className="flex shrink-0 flex-wrap items-center gap-2 border-b border-csm-line bg-white px-3 py-2">
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-csm-ink4" aria-hidden />
              <input type="text" value={filter} onChange={e => setFilter(e.target.value)} placeholder={t('common.filter', 'Filter…')}
                className="h-8 w-72 rounded-csm-control border border-csm-border pl-8 pr-2 text-csm-md focus:border-primary focus:outline-none" />
            </div>
            <FacetFilter label={t('sfk.conflict_rule.facet.severity', 'Severity')} options={severities.map(s => ({ value: s.code, label: s.name }))} selected={severityFacet} onChange={setSeverityFacet} />
            <FacetFilter label={t('common.boolean.active', 'Active')} options={[{ value: 'Y', label: t('common.boolean.yes', 'Yes') }, { value: 'N', label: t('common.boolean.no', 'No') }]} selected={activeFacet} onChange={setActiveFacet} />
            <ClearFiltersButton active={filter.trim() !== '' || severityFacet.length > 0 || activeFacet.length > 0} onClear={() => { setFilter(''); setSeverityFacet([]); setActiveFacet([]); }} />
            <div className="ml-auto flex items-center gap-2">
              {busy && <span className="text-csm-xs text-csm-muted">{t('common.loading', 'Loading…')}</span>}
              <PersonalizationToggle />
            </div>
          </div>
          <div className="flex min-h-0 flex-1 overflow-hidden">
        <div className="flex-1 overflow-auto">
          <PersonalizedGrid componentKey="grid" columns={GRID_COLS}>
            {(visibleCols, widths) => (
              <table className={GRID_TABLE_CLASS}>
                <thead className="sticky top-0 z-10 bg-csm-surface-alt text-csm-xs uppercase tracking-wider text-csm-ink-soft">
                  <tr>
                    <th className="border-b border-csm-border px-1 py-1.5 text-left" style={{ width: '1%' }} aria-hidden>&nbsp;</th>
                    {visibleCols.map(col => (
                      <th key={col.key} className="border-b border-csm-border px-3 py-1.5 text-left" style={widths[col.key] ? { width: widths[col.key] } : undefined}>{col.header}</th>
                    ))}
                    <th className="border-b border-csm-border px-3 py-1.5 text-left" style={{ width: '1%' }}>{t('common.actions', 'Actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(r => {
                    const { a, b } = sidesOf(r.rule_id);
                    return (
                      <Fragment key={r.rule_id}>
                      <tr className={`hover:bg-csm-surface-alt ${selected?.rule_id === r.rule_id ? 'bg-csm-surface-alt' : ''}`}>
                        <td className="border-b border-csm-border-soft px-1 py-1.5 text-center align-middle" onClick={e => e.stopPropagation()}>
                          <RelatedExpandButton open={detail.isOpen(r.rule_id)} hasChildren={hasRelated(r.rule_id)} onToggle={() => detail.toggle(r.rule_id)}
                            expandLabel={t('common.related.expand', 'Show related records')} collapseLabel={t('common.related.collapse', 'Hide related')} tooltip={t('common.related.tooltip', 'Show related records')} />
                        </td>
                        {visibleCols.map(col => (
                          <td key={col.key} className="border-b border-csm-border-soft px-3 py-1.5" style={widths[col.key] ? { width: widths[col.key] } : undefined}>
                            {col.key === 'display_text' && tlName(r.tl, 'EN_US')}
                            {col.key === 'internal_code' && <span className="font-mono text-csm-sm">{r.internal_code}</span>} {/* fk-display-ok: rule's own code */}
                            {col.key === 'severity' && resolveNameForSeverity(r.rule_severity_code)}
                            {col.key === 'op_state' && resolveNameForOpState(r.rule_op_state_code)}
                            {col.key === 'sides' && (a && b ? `${bundleName(a.bundle_id)} ⟷ ${bundleName(b.bundle_id)}` : '—')}
                            {col.key === 'is_active' && <YesNoBadge value={r.is_active} />}
                          </td>
                        ))}
                        <td className="border-b border-csm-border-soft px-3 py-1.5 text-right">
                          <button onClick={() => void runPreview(r)} className="mr-1 rounded border border-csm-border bg-white px-2 py-0.5 text-csm-xs hover:bg-csm-border-soft">{t('sfk.conflict_rule.preview', 'Preview')}</button>
                          {canChange && (<button onClick={() => setEditing(r)} className="rounded border border-csm-border bg-white px-2 py-0.5 text-csm-xs hover:bg-csm-border-soft">{t('common.edit', 'Edit')}</button>)}
                        </td>
                      </tr>
                      {detail.isOpen(r.rule_id) && (
                        <tr>
                          <td colSpan={visibleCols.length + 2} className="border-b border-csm-border bg-csm-surface-alt/40 px-3 py-3" onClick={e => e.stopPropagation()}>
                            <RelatedChildrenPanel groups={buildRelatedGroups(r.rule_id)} emptyLabel={t('common.related.none', 'No related records.')} />
                          </td>
                        </tr>
                      )}
                      </Fragment>
                    );
                  })}
                  {filtered.length === 0 && !busy && (
                    <tr><td colSpan={visibleCols.length + 2} className="py-12 text-center"><EmptyState title={t('common.no_records', 'No records')} /></td></tr>
                  )}
                </tbody>
              </table>
            )}
          </PersonalizedGrid>
        </div>

        {selected && (
          <div className="flex w-96 shrink-0 flex-col border-l border-csm-border bg-white">
            <div className="border-b border-csm-border p-2 text-csm-sm font-medium text-csm-ink-soft">
              {t('sfk.conflict_rule.live_matches', 'Live matches')} — {tlName(selected.tl, 'EN_US')}
            </div>
            <div className="flex-1 overflow-auto p-2">
              {previewBusy && <div className="text-csm-xs text-csm-watermark">{t('common.loading', 'Loading…')}</div>}
              {!previewBusy && preview && preview.length === 0 && <EmptyState title={t('sfk.conflict_rule.no_matches', 'No current matches')} />}
              {!previewBusy && preview && preview.map((p, i) => (
                <div key={i} className="mb-1 rounded border border-csm-border-soft px-2 py-1 text-csm-xs">
                  {p.app_user_id != null ? `${t('sfk.conflict_rule.subject', 'Subject')} #${p.app_user_id}` : `${t('sfk.conflict_rule.self_user', 'SOFSEK operator')} #${p.self_user_id}`}
                  <span className="ml-2 text-csm-watermark">A:[{p.matched_permission_ids_a?.join(',')}] B:[{p.matched_permission_ids_b?.join(',')}]</span>
                </div>
              ))}
            </div>
          </div>
        )}
          </div>
        </ContentCard>
      </div>

      {(editing || creating) && (
        <RuleModal session={session} row={editing} bundles={bundles} severities={severities} opStates={opStates} ruleBundles={ruleBundles}
          onClose={() => { setEditing(null); setCreating(false); }}
          onSaved={() => { setEditing(null); setCreating(false); void reload(); }} />
      )}
    </div>
  );
}

function RuleModal({ session, row, bundles, severities, opStates, ruleBundles, onClose, onSaved }: {
  session: AuthSession; row: RuleRow | null; bundles: CodeOpt[]; severities: CodeOpt[]; opStates: CodeOpt[]; ruleBundles: RuleBundleRow[];
  onClose: () => void; onSaved: () => void;
}) {
  const { t } = useTranslation();
  const isCreate = row === null;
  const existingSideA = row ? ruleBundles.find(rb => rb.rule_id === row.rule_id && rb.side_ordinal === 1) : undefined;
  const existingSideB = row ? ruleBundles.find(rb => rb.rule_id === row.rule_id && rb.side_ordinal === 2) : undefined;

  const [code, setCode] = useState(row?.internal_code ?? '');
  const [severity, setSeverity] = useState(row?.rule_severity_code ?? severities[0]?.code ?? '');
  const [opState, setOpState] = useState(row?.rule_op_state_code ?? 'ACTIVE');
  const [bundleA, setBundleA] = useState(existingSideA ? String(existingSideA.bundle_id) : bundles[0]?.code ?? '');
  const [bundleB, setBundleB] = useState(existingSideB ? String(existingSideB.bundle_id) : bundles[1]?.code ?? bundles[0]?.code ?? '');
  const [isActive, setIsActive] = useState(!row || row.is_active === 'Y');
  const [translations, setTranslations] = useState<TranslationRow[]>(row?.tl ?? ACTIVE_LOCALES.map(loc => ({ locale_code: loc, display_text: '' })));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    setBusy(true); setError(null);
    try {
      if (!code.trim()) throw new Error(t('common.err.code_required', 'Code is required.'));
      if (!translations.some(tr => tr.display_text?.trim())) throw new Error(t('common.err.translation_required', 'At least one translation is required.'));
      if (bundleA === bundleB) throw new Error(t('sfk.conflict_rule.err.same_bundle', 'Bundle A and Bundle B must be different.'));

      const payload = { rule_severity_code: severity, rule_op_state_code: opState, is_active: isActive ? 'Y' : 'N' };
      let ruleId = row?.rule_id;
      if (row === null) {
        const { row_id, error: e } = await routedInsert(session, 'sfk_conflict_rule', { internal_code: code.trim().toUpperCase(), ...payload });
        if (e) throw new Error(e.message);
        ruleId = row_id ?? undefined;
      } else {
        const { error: e } = await routedUpdate(session, 'sfk_conflict_rule', 'rule_id', row.rule_id, payload);
        if (e) throw new Error(e.message);
      }
      if (ruleId != null) {
        await Promise.all(
          translations.filter(tr => tr.display_text?.trim()).map(tr =>
            routedInsert(session, 'sfk_conflict_rule_tl', { rule_id: ruleId, locale_code: tr.locale_code, display_text: tr.display_text }))
        );
        if (existingSideA) {
          if (String(existingSideA.bundle_id) !== bundleA) await routedUpdate(session, 'sfk_rule_bundle', 'rule_bundle_id', existingSideA.rule_bundle_id, { bundle_id: Number(bundleA) });
        } else {
          await routedInsert(session, 'sfk_rule_bundle', { rule_id: ruleId, bundle_id: Number(bundleA), side_ordinal: 1 });
        }
        if (existingSideB) {
          if (String(existingSideB.bundle_id) !== bundleB) await routedUpdate(session, 'sfk_rule_bundle', 'rule_bundle_id', existingSideB.rule_bundle_id, { bundle_id: Number(bundleB) });
        } else {
          await routedInsert(session, 'sfk_rule_bundle', { rule_id: ruleId, bundle_id: Number(bundleB), side_ordinal: 2 });
        }
      }
      onSaved();
    } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  };

  return (
    <Modal
      title={isCreate ? t('sfk.conflict_rule.new', 'New Conflict Rule') : `${t('common.edit', 'Edit')} — ${row?.internal_code ?? ''}`}
      onClose={onClose}
      width="md"
      headerAction={<PersonalizationToggle />}
      footer={
        <>
          <button onClick={onClose} className="rounded border border-csm-border bg-white px-3 py-1 text-csm-sm hover:bg-csm-border-soft">{t('common.cancel', 'Cancel')}</button>
          <button onClick={() => void save()} disabled={busy} className="rounded border border-primary bg-primary px-3 py-1 text-csm-sm text-white hover:bg-primary-hover disabled:opacity-60">{busy ? t('common.saving', 'Saving…') : t('common.save', 'Save')}</button>
        </>
      }
    >
      <PersonalizedField componentKey="modal" fieldKey="internal_code" label={t('common.code', 'Code')}>
        <label className={labelCls}>{t('common.code', 'Code')}</label>
        <input value={code} disabled={!isCreate} onChange={e => setCode(e.target.value.toUpperCase())} className={`${inputCls} font-mono`} />
      </PersonalizedField>
      <div className="grid grid-cols-2 gap-x-3">
        <PersonalizedField componentKey="modal" fieldKey="rule_severity_code" label={t('sfk.conflict_rule.severity', 'Severity')}>
          <label className={labelCls}>{t('sfk.conflict_rule.severity', 'Severity')}</label>
          <select value={severity} onChange={e => setSeverity(e.target.value)} className={`${inputCls} bg-white`}>
            {severities.map(s => <option key={s.code} value={s.code}>{s.name}</option>)}
          </select>
        </PersonalizedField>
        <PersonalizedField componentKey="modal" fieldKey="rule_op_state_code" label={t('sfk.conflict_rule.op_state', 'Operational State')}>
          <label className={labelCls}>{t('sfk.conflict_rule.op_state', 'Operational State')}</label>
          <select value={opState} onChange={e => setOpState(e.target.value)} className={`${inputCls} bg-white`}>
            {opStates.map(s => <option key={s.code} value={s.code}>{s.name}</option>)}
          </select>
        </PersonalizedField>
      </div>
      <div className="grid grid-cols-2 gap-x-3">
        <PersonalizedField componentKey="modal" fieldKey="bundle_a" label={t('sfk.conflict_rule.bundle_a', 'Bundle A')}>
          <label className={labelCls}>{t('sfk.conflict_rule.bundle_a', 'Bundle A')}</label>
          <select value={bundleA} onChange={e => setBundleA(e.target.value)} className={`${inputCls} bg-white`}>
            {bundles.map(b => <option key={b.code} value={b.code}>{b.name}</option>)}
          </select>
        </PersonalizedField>
        <PersonalizedField componentKey="modal" fieldKey="bundle_b" label={t('sfk.conflict_rule.bundle_b', 'Bundle B')}>
          <label className={labelCls}>{t('sfk.conflict_rule.bundle_b', 'Bundle B')}</label>
          <select value={bundleB} onChange={e => setBundleB(e.target.value)} className={`${inputCls} bg-white`}>
            {bundles.map(b => <option key={b.code} value={b.code}>{b.name}</option>)}
          </select>
        </PersonalizedField>
      </div>
      <PersonalizedField componentKey="modal" fieldKey="is_active" label={t('common.boolean.active', 'Active')}>
        <label className="mb-3 flex items-center gap-2 text-csm-sm text-csm-ink-soft">
          <input type="checkbox" checked={isActive} onChange={e => setIsActive(e.target.checked)} className="h-4 w-4 accent-gray-700" />
          {t('common.boolean.active', 'Active')}
        </label>
      </PersonalizedField>

      <TranslationsEditor availableLocales={ACTIVE_LOCALES} value={translations} onChange={setTranslations} label={t('common.translations', 'Translations')} />

      {error && <div className="mt-3 rounded border border-csm-danger-border bg-csm-danger-soft px-2 py-1 text-csm-sm text-csm-danger">{error}</div>}
    </Modal>
  );
}

export function SfkConflictRulePage({ session }: { session: AuthSession }) {
  return (
    <PersonalizationProvider pageCode={PAGE_CODE} session={session}>
      <ConflictRuleInner session={session} />
    </PersonalizationProvider>
  );
}
